// Detecção do "corpo" de um loop e constantes de layout do container.
// Usado tanto na simulação (runsService) quanto na renderização do canvas (flowStore),
// para que o nó Loop seja desenhado como um CONTAINER que envolve os nós-filhos.
import type { FlowNode, FlowEdge } from '../types';

// Dimensões estimadas de um nó (antes da medição real do React Flow) e padding do container.
export const EST_NODE_W = 200;
export const EST_NODE_H = 96;
export const LOOP_PAD_X = 36;
export const LOOP_PAD_TOP = 64; // espaço para o header do container
export const LOOP_PAD_BOTTOM = 36;

// Corpo do loop: união de (a) filhos do container via parentId e
// (b) nós alcançáveis a partir do loop que retornam a ele via loopback (compat. seeds antigos).
export function computeLoopBody(
  nodes: FlowNode[],
  edges: FlowEdge[],
  loopId: string,
): Set<string> {
  const body = new Set<string>();
  // (a) membros do container (modelo canônico)
  nodes.forEach((n) => {
    if (n.parentId === loopId) body.add(n.id);
  });

  // (b) fallback por arestas de loopback
  const forward = edges.filter((e) => !e.isLoopBack);
  const loopbackSources = edges
    .filter((e) => e.isLoopBack && e.target === loopId)
    .map((e) => e.source);
  if (!loopbackSources.length) return body;

  // Nós alcançáveis a partir do loop (sem voltar pelo próprio loop).
  const reachable = new Set<string>();
  const stack = forward.filter((e) => e.source === loopId).map((e) => e.target);
  while (stack.length) {
    const id = stack.pop()!;
    if (reachable.has(id) || id === loopId) continue;
    reachable.add(id);
    forward.filter((e) => e.source === id).forEach((e) => stack.push(e.target));
  }

  // Mantém só os que conseguem voltar a um source de loopback.
  reachable.forEach((id) => {
    const seen = new Set<string>();
    const st = [id];
    while (st.length) {
      const cur = st.pop()!;
      if (seen.has(cur)) continue;
      seen.add(cur);
      if (loopbackSources.includes(cur)) {
        body.add(id);
        break;
      }
      forward.filter((e) => e.source === cur).forEach((e) => st.push(e.target));
    }
  });
  loopbackSources.forEach((s) => body.add(s));
  return body;
}
